import type {
  CreateDisplayRequest,
  DisplayDetail,
  ReleaseSummary,
} from "@led-signage/shared/management-api";
import { useState } from "react";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { api } from "./api/index.js";
import { useResource } from "./api/useResource.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { PermissionNotice, useCan } from "./session.js";

/**
 * LEDの追加・改名・撤去と、端末の退役。
 *
 * これまでD1へ直接SQLを打っていた操作である。**監査ログが残らない経路を
 * 無くすことが目的**なので、理由を必須にする操作はそのまま理由を求める。
 */

type Action =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "failed"; failure: ActionFailure };

function timestamp(value: string | null): string {
  if (value === null) return "—";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

export function DisplayManagement() {
  const [reloadToken, setReloadToken] = useState(0);
  const [includeArchived, setIncludeArchived] = useState(false);
  const displays = useResource(
    (signal) => api.listDisplays(signal),
    [reloadToken],
  );
  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  return (
    <section className="panel display-management">
      <h3>LEDと端末の管理</h3>
      <p className="resource-note">
        LEDの追加・改名・撤去と、端末の退役を行います。すべて監査ログへ残ります。
      </p>

      <PermissionNotice permission="display:manage" action="LEDの管理" />

      <CreateDisplay onCreated={reload} />

      <label className="audit-filter">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(event) => {
            setIncludeArchived(event.target.checked);
          }}
        />
        撤去済みも表示する
      </label>

      {displays.state === "loading" && (
        <p className="resource-note">読み込み中…</p>
      )}
      {displays.state === "error" && (
        <div className="inline-alert inline-alert--error">
          <b>LEDの一覧を読み込めませんでした</b>
          <span>{displays.message}</span>
        </div>
      )}
      {displays.state === "ready" && (
        <DisplayList
          rows={displays.value.items}
          includeArchived={includeArchived}
          onChanged={reload}
        />
      )}
    </section>
  );
}

function DisplayList({
  rows,
  includeArchived,
  onChanged,
}: {
  rows: readonly DisplayDetail[];
  includeArchived: boolean;
  onChanged: () => void;
}) {
  /*
   * 一覧APIは既定でアーカイブ済みを除外する。ここで再度絞ると二重になるが、
   * 表示の切り替えだけのために再取得しないほうが操作が軽い。
   */
  const visible = includeArchived
    ? rows
    : rows.filter((row) => row.display.archivedAt === null);

  if (visible.length === 0) {
    return (
      <p className="resource-note">
        登録されているLEDがありません。上のフォームから追加してください。
      </p>
    );
  }

  return (
    <div className="display-manage-list">
      {visible.map((row) => (
        <DisplayRow key={row.display.id} row={row} onChanged={onChanged} />
      ))}
    </div>
  );
}

type OpenAction = "rename" | "revoke" | "rollback" | "archive" | null;

function DisplayRow({
  row,
  onChanged,
}: {
  row: DisplayDetail;
  onChanged: () => void;
}) {
  /*
   * 開いている操作は常に1つにする。確認パネルが同時に2つ出ると、どちらの
   * 理由欄へ入力しているのか分からなくなる。
   */
  const [open, setOpen] = useState<OpenAction>(null);
  const archived = row.display.archivedAt !== null;
  const device = row.device;
  const hasActiveDevice = device !== null && device.retiredAt === null;
  const canManageDisplay = useCan("display:manage");
  const canManageDevice = useCan("device:manage");
  const close = () => {
    setOpen(null);
  };
  const done = () => {
    setOpen(null);
    onChanged();
  };

  return (
    <div className={`display-manage-row${archived ? " is-archived" : ""}`}>
      <div className="display-manage-row__head">
        <span>
          <b>{row.display.name}</b>
          <small>{row.display.id}</small>
        </span>
        {archived ? (
          <span className="state-tag state-tag--warn">
            撤去済み {timestamp(row.display.archivedAt)}
          </span>
        ) : (
          <span className="state-tag state-tag--ok">運用中</span>
        )}
      </div>

      <div className="display-manage-row__body">
        <span>
          端末:{" "}
          {device === null ? (
            <em>未登録</em>
          ) : (
            <>
              {device.name}
              {device.enrolledAt === null && <em>（未enroll）</em>}
            </>
          )}
        </span>
        <span>タイムゾーン: {row.display.timezone}</span>
        {row.desiredRelease !== null && (
          <span>配信版: 版 {row.desiredRelease.version}</span>
        )}
      </div>

      {!archived && open === null && (
        <div className="display-manage-row__actions">
          <button
            type="button"
            disabled={!canManageDisplay}
            onClick={() => {
              setOpen("rename");
            }}
          >
            名前を変更
          </button>
          {row.desiredRelease !== null && (
            <button
              type="button"
              disabled={!canManageDisplay}
              onClick={() => {
                setOpen("rollback");
              }}
            >
              配信版を1つ前へ戻す
            </button>
          )}
          {hasActiveDevice && (
            <button
              type="button"
              className="danger-button"
              disabled={!canManageDevice}
              onClick={() => {
                setOpen("revoke");
              }}
            >
              端末を退役させる
            </button>
          )}
          <button
            type="button"
            className="danger-button"
            disabled={!canManageDisplay}
            onClick={() => {
              setOpen("archive");
            }}
          >
            このLEDを撤去する
          </button>
        </div>
      )}

      {open === "rename" && (
        <RenameDisplay row={row} onDone={done} onCancel={close} />
      )}
      {open === "rollback" && (
        <RollbackDisplay row={row} onDone={done} onCancel={close} />
      )}
      {open === "revoke" && device !== null && (
        <RevokeDevice
          deviceId={device.id}
          deviceName={device.name}
          onDone={done}
          onCancel={close}
        />
      )}
      {open === "archive" && (
        <ArchiveDisplay
          row={row}
          hasActiveDevice={hasActiveDevice}
          onDone={done}
          onCancel={close}
        />
      )}
    </div>
  );
}

function CreateDisplay({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Asia/Tokyo");
  const [action, setAction] = useState<Action>({ kind: "idle" });
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const allowed = useCan("display:manage");

  async function create() {
    setAction({ kind: "running" });
    const input: CreateDisplayRequest = { name: name.trim(), timezone };
    try {
      await api.createDisplay(input, idempotencyKey);
      setName("");
      setIdempotencyKey(newIdempotencyKey());
      setAction({ kind: "idle" });
      onCreated();
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <div className="display-create">
      <h4>LEDを追加</h4>
      <label className="field">
        <span>名前</span>
        <input
          type="text"
          value={name}
          placeholder="例: 那覇メインLED"
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>
      <label className="field">
        <span>タイムゾーン</span>
        <input
          type="text"
          value={timezone}
          onChange={(event) => {
            setTimezone(event.target.value);
          }}
        />
      </label>

      {action.kind === "failed" && (
        <div className="inline-alert inline-alert--error">
          <b>追加できませんでした</b>
          <span>{action.failure.message}</span>
          <AuthenticationRecoveryButton failure={action.failure} />
        </div>
      )}

      <div className="editor-actions">
        <button
          type="button"
          disabled={
            !allowed || name.trim().length === 0 || action.kind === "running"
          }
          onClick={() => void create()}
        >
          {action.kind === "running" ? "追加中…" : "追加する"}
        </button>
      </div>
    </div>
  );
}

function RenameDisplay({
  row,
  onDone,
  onCancel,
}: {
  row: DisplayDetail;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.display.name);
  const [action, setAction] = useState<Action>({ kind: "idle" });

  async function rename() {
    setAction({ kind: "running" });
    try {
      await api.updateDisplay(
        row.display.id,
        { name: name.trim() },
        row.display.etag,
      );
      onDone();
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <div className="display-rename">
      <label className="field">
        <span>新しい名前</span>
        <input
          type="text"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>

      {action.kind === "failed" && (
        <div className="inline-alert inline-alert--error">
          <b>変更できませんでした</b>
          <span>{action.failure.message}</span>
          {action.failure.code === "VERSION_CONFLICT" && (
            <span>
              他の担当者が先に更新しています。読み込み直してからやり直して
              ください。
            </span>
          )}
          <AuthenticationRecoveryButton failure={action.failure} />
        </div>
      )}

      <div className="editor-actions">
        <button
          type="button"
          disabled={name.trim().length === 0 || action.kind === "running"}
          onClick={() => void rename()}
        >
          {action.kind === "running" ? "変更中…" : "保存"}
        </button>
        <button type="button" onClick={onCancel}>
          やめる
        </button>
      </div>
    </div>
  );
}

function RevokeDevice({
  deviceId,
  deviceName,
  onDone,
  onCancel,
}: {
  deviceId: string;
  deviceName: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [action, setAction] = useState<Action>({ kind: "idle" });

  async function revoke() {
    setAction({ kind: "running" });
    try {
      await api.revokeDevice(deviceId, { reason: reason.trim() });
      onDone();
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <div className="inline-alert inline-alert--error">
      <b>{deviceName} を退役させます</b>
      <span>
        <strong>この端末は以後クラウドへ接続できなくなります。</strong>
        資格情報を失効させるため、同じ端末を使うには登録し直しが必要です。
        現地の再生は端末内のデータで続きますが、更新は届きません。
      </span>
      <label className="field">
        <span>理由（監査ログへ残ります）</span>
        <input
          type="text"
          value={reason}
          placeholder="例: 機器故障のため交換"
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>

      {action.kind === "failed" && (
        <>
          <span>{action.failure.message}</span>
          <AuthenticationRecoveryButton failure={action.failure} />
        </>
      )}

      <div className="editor-actions">
        <button
          type="button"
          className="danger-button"
          disabled={reason.trim().length === 0 || action.kind === "running"}
          onClick={() => void revoke()}
        >
          {action.kind === "running" ? "退役中…" : "退役させる"}
        </button>
        <button type="button" onClick={onCancel}>
          やめる
        </button>
      </div>
    </div>
  );
}

function ArchiveDisplay({
  row,
  hasActiveDevice,
  onDone,
  onCancel,
}: {
  row: DisplayDetail;
  hasActiveDevice: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [action, setAction] = useState<Action>({ kind: "idle" });
  const [idempotencyKey] = useState(newIdempotencyKey);

  async function archive() {
    setAction({ kind: "running" });
    try {
      await api.archiveDisplay(
        row.display.id,
        { reason: reason.trim() },
        idempotencyKey,
      );
      onDone();
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <div className="inline-alert inline-alert--error">
      <b>{row.display.name} を撤去します</b>
      <span>
        一覧から外れ、新しい承認依頼の対象にできなくなります。データは残るので、
        操作履歴からは追えます。
      </span>
      {hasActiveDevice && (
        <span>
          <strong>先に端末を退役させてください。</strong>
          端末が残ったまま撤去すると、その端末は存在しないLEDへ同期し続けます。
        </span>
      )}
      <label className="field">
        <span>理由（監査ログへ残ります）</span>
        <input
          type="text"
          value={reason}
          placeholder="例: 店舗閉鎖のため撤去"
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>

      {action.kind === "failed" && (
        <>
          <span>{action.failure.message}</span>
          {action.failure.code === "DISPLAY_IN_USE" && (
            <span>
              退役していない端末が残っています。先に端末を退役させてください。
            </span>
          )}
          <AuthenticationRecoveryButton failure={action.failure} />
        </>
      )}

      <div className="editor-actions">
        <button
          type="button"
          className="danger-button"
          disabled={
            reason.trim().length === 0 ||
            hasActiveDevice ||
            action.kind === "running"
          }
          onClick={() => void archive()}
        >
          {action.kind === "running" ? "撤去中…" : "撤去する"}
        </button>
        <button type="button" onClick={onCancel}>
          やめる
        </button>
      </div>
    </div>
  );
}

/**
 * 配信版を1つ前へ戻す。
 *
 * **戻し先を事前に表示できない。** Display単位でReleaseを一覧するendpointが
 * 契約に無く、省略時の選択（現在より小さい最大version）はサーバーが決める。
 * 実行後に、どれへ戻ったかを出す。
 */
function RollbackDisplay({
  row,
  onDone,
  onCancel,
}: {
  row: DisplayDetail;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [action, setAction] = useState<Action>({ kind: "idle" });
  const [rolledBack, setRolledBack] = useState<ReleaseSummary | null>(null);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const current = row.desiredRelease;

  async function rollback() {
    setAction({ kind: "running" });
    try {
      const result = await api.rollbackDisplay(
        row.display.id,
        { reason: reason.trim() },
        idempotencyKey,
      );
      setRolledBack(result);
      setAction({ kind: "idle" });
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  if (rolledBack !== null) {
    return (
      <div className="inline-alert">
        <b>版 {rolledBack.version} へ戻しました</b>
        <span>
          端末は次に通信したときにこの版を取得します。切り替わるまで現地では
          前の内容が流れ続けます。
        </span>
        <button type="button" onClick={onDone}>
          閉じる
        </button>
      </div>
    );
  }

  if (current === null) {
    return null;
  }

  return (
    <div className="inline-alert">
      <b>いま配信中の版 {current.version} を、1つ前へ戻します</b>
      <span>
        <strong>どの版へ戻るかは、実行してから表示します。</strong>
        版を一覧する手段がまだないため、事前に確認できません。戻した先も
        問題があれば、もう一度実行するとさらに前へ戻ります。
      </span>
      <span>
        新しい承認依頼は作りません。戻し先は以前に承認・公開された版なので、
        二者承認なしで実行できます。
      </span>
      <label className="field">
        <span>理由（監査ログへ残ります）</span>
        <input
          type="text"
          value={reason}
          placeholder="例: 誤った内容が表示されていたため"
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>

      {action.kind === "failed" && (
        <>
          <span>{action.failure.message}</span>
          {action.failure.code === "NO_ROLLBACK_TARGET" && (
            <span>
              このLEDには戻せる過去の版がありません。いまの版が最初の公開です。
            </span>
          )}
          <AuthenticationRecoveryButton failure={action.failure} />
        </>
      )}

      <div className="editor-actions">
        <button
          type="button"
          disabled={reason.trim().length === 0 || action.kind === "running"}
          onClick={() => void rollback()}
        >
          {action.kind === "running" ? "戻しています…" : "1つ前へ戻す"}
        </button>
        <button type="button" onClick={onCancel}>
          やめる
        </button>
      </div>
    </div>
  );
}
